import { type SetStateAction, useEffect, useState } from 'react';
import { useIntl } from '@atlassian/jira-intl';
import type { CreateOrEditPriorityScheme } from '../../common/ui/types';
import { type OnError, validateName, validateDescription, validatePriorities } from './utils';

interface PrioritySchemeDetailsHookReturn {
	priorityScheme: CreateOrEditPriorityScheme;
	onPrioritySchemeDetailsChange: (prioritySchemeDetails: CreateOrEditPriorityScheme) => void;
	triggerSchemeDetailsValidation: (prioritySchemeDetails: CreateOrEditPriorityScheme) => boolean;
}

export const usePrioritySchemeDetailsController = (
	initialPriorityScheme: CreateOrEditPriorityScheme,
	setFormError: (
		value: SetStateAction<{
			name: string;
			description: string;
			priorityIds: string;
			projectIds: string;
		}>,
	) => void,
): PrioritySchemeDetailsHookReturn => {
	const { formatMessage } = useIntl();

	const [prioritySchemeDetails, setPrioritySchemeDetails] =
		useState<CreateOrEditPriorityScheme>(initialPriorityScheme);

	useEffect(() => {
		setPrioritySchemeDetails(initialPriorityScheme);
	}, [initialPriorityScheme]);

	const onValidationError: OnError = (field, messageDescriptor, optionalValues) => {
		setFormError((fields) => ({
			...fields,
			[field]: formatMessage(messageDescriptor, optionalValues),
		}));
	};

	const onPrioritySchemeDetailsChange = (incomingPriorityScheme: CreateOrEditPriorityScheme) => {
		if (incomingPriorityScheme.name !== prioritySchemeDetails.name) {
			const isClean = validateName(incomingPriorityScheme.name, onValidationError);
			if (isClean) {
				setFormError((fields) => ({
					...fields,
					name: '',
				}));
			}
		}
		if (incomingPriorityScheme.description !== prioritySchemeDetails.description) {
			const isClean = validateDescription(incomingPriorityScheme.description);
			if (isClean) {
				setFormError((fields) => ({
					...fields,
					description: '',
				}));
			}
		}
		if (incomingPriorityScheme.priorities.length !== prioritySchemeDetails.priorities.length) {
			const isClean = validatePriorities(incomingPriorityScheme.priorities, onValidationError);
			if (isClean) {
				setFormError((fields) => ({
					...fields,
					priorityIds: '',
				}));
			}
		}

		setPrioritySchemeDetails(incomingPriorityScheme);
	};

	const triggerSchemeDetailsValidation = (incomingPriorityScheme: CreateOrEditPriorityScheme) => {
		let isClean = true;

		isClean = validateName(incomingPriorityScheme.name, onValidationError) && isClean;
		isClean = validatePriorities(incomingPriorityScheme.priorities, onValidationError) && isClean;

		return isClean;
	};

	return {
		onPrioritySchemeDetailsChange,
		priorityScheme: prioritySchemeDetails,
		triggerSchemeDetailsValidation,
	};
};
